// ---------------------------------------------------------------------------
// Calculation engine — real, not a black box.
//
// The original invokes EvalHid_cal.exe (.NET 6 self-contained) via Shell(),
// with a pure CSV input/output file contract — but the embedded application
// logic DLL (Evalhid.dll) can be decompiled (ilspycmd), giving full
// visibility into the 8 real formulas. This module implements the catalog's
// 6 rainfall-runoff models (Témez, SAC, HBV, GR2M, GR4J, AWBM) and the 2
// snow ones (Nieve1, Nieve2) — see REAL_MODELS/REAL_SNOW_MODELS below, and
// the header of each `src/engine/<model>.ts` for the real differences found
// against the documentation (see CLAUDE.md). For a model code that doesn't
// exist in that catalog (shouldn't happen with EvalHid's current models, but
// covers corrupt data or a future model), it falls back to a generic
// approximation: a moisture reservoir + a linear recession reservoir, using
// the assigned model's real capacity/recession parameters so the response
// magnitude is coherent, not arbitrary.
//
// I/O contract mirrors the real .exe: per sub-basin, a daily series aligned
// with the input precipitation/ETP dates.
// ---------------------------------------------------------------------------
import type { DailySeries } from "../model/dailySeries.ts";
import { hasColumnAt } from "../model/dailySeries.ts";
import type {
  SubBasin,
  EvalHidProject,
  ParameterValue,
  StateVariableValue,
} from "../model/types.ts";
import { NO_MODEL } from "../model/projectEntities.ts";
import { activeUnitsCoefficient } from "../model/units.ts";
import {
  REAL_MODELS,
  REAL_SNOW_MODELS,
  computeAggregationDetail,
  realModelCodes,
  realSnowModelCodes,
  type RealModelName,
  type RealSnowModel,
} from "./modelRegistry.ts";

const DEFAULT_CAPACITY_MM = 150;
const DEFAULT_RECESSION = 0.1;
/** Fraction of the surplus that leaves as fast runoff (the rest feeds the recession reservoir). */
const FAST_RUNOFF_FRACTION = 0.3;

export interface SubBasinResult {
  subBasinCode: number;
  description: string;
  /** mm/day, aligned with the input precipitation series' `dates`. */
  contribution: Float64Array;
  /**
   * Name of the real model that produced `contribution` (`"Témez"`, `"HBV"`...)
   * — absent if the generic approximation was used (see file header). Used
   * in `engine/aggregation.ts` to know which `detail` keys correspond to
   * each model's surface/groundwater runoff (not every model exposes them
   * the same way, or at all).
   */
  model?: RealModelName;
  /**
   * Step-by-step internal state series of the model (and, if applicable, of
   * the preceding snow model, prefixed with `snow_`) — the equivalent of
   * the real .exe's "Partial results"/Sal_parciales.csv, always calculated
   * (doesn't depend on any checkbox, see calculationOptions.ts). Absent if
   * the sub-basin used the generic approximation (model without a real
   * engine yet).
   *
   * `calculateProjectAsync` (the main "Calculate" button's own path, whose
   * whole `SubBasinResult[]` is retained in `state.results` for as long as
   * the project stays open) deliberately DROPS this field once it's fed
   * `aggregationDetail` below — real incident, a Duero-scale project (1728
   * sub-basins) retaining full `detail` for every one of them cost ~3GB,
   * dwarfing even the input series. `calculateProject` (the sync path,
   * used by calibration and directly by tests) still returns it in full —
   * its callers never retain more than one evaluation's worth at a time.
   * `DetailChart.tsx`'s "Ver detalle" view recomputes ONE sub-basin's full
   * `detail` on demand instead (see `useSubBasinDetail.ts`) — cheap
   * (milliseconds, this was never a CPU problem) since `state`  already
   * keeps everything `calculateOneSubBasin` needs.
   */
  detail?: Record<string, Float64Array>;
  /**
   * The 2 arrays `aggregation.ts`'s `aggregateByKey` actually needs from
   * `detail` (surface/groundwater runoff, `modelRegistry.ts`'s
   * `SUP_SUBT_KEYS`) — precomputed here so the full `detail` object above
   * can be dropped from `calculateProjectAsync`'s bulk results without
   * losing the surface/groundwater breakdown for every outlet point/zone/
   * aquifer. `undefined` under the exact same conditions `detail` itself
   * would make the breakdown unavailable (generic approximation, or a
   * model not listed in `SUP_SUBT_KEYS` — SAC/GR2M's own case, see there).
   */
  aggregationDetail?: { surface: Float64Array; groundwater: Float64Array };
}

/**
 * `(kind, zoneCode)` -> its rows of a values table (ParameterValue/
 * StateVariableValue) — built once per `calculateProject`/
 * `calculateProjectAsync` call and reused for every sub-basin, instead of
 * each sub-basin re-scanning the whole table itself (`valueByZone` used to
 * `.filter()` it directly): in a v1.0 project (one zone per sub-basin, see
 * CLAUDE.md's "Compatibility with v1.0 projects"), that table's size grows
 * with the sub-basin count too, so re-scanning it per sub-basin was
 * quadratic overall. Harmless for the smaller v2 projects with a handful of
 * shared zones, but this indexing is O(1) either way.
 */
type ZoneValueIndex<TVal> = Map<string, TVal[]>;

function indexByZone<TVal extends { zoneCode: number | null; kind: number }>(
  values: TVal[]
): ZoneValueIndex<TVal> {
  const index = new Map<string, TVal[]>();
  for (const v of values) {
    const key = `${v.kind}:${v.zoneCode}`;
    const bucket = index.get(key);
    if (bucket) bucket.push(v);
    else index.set(key, [v]);
  }
  return index;
}

/**
 * `indexByZone`'s own sibling table (parameter/state variable DEFINITIONS,
 * not values) never got the same treatment — `valueByZone` below still
 * `.filter()`ed `project.parameterDefinitions`/`stateVariableDefinitions`
 * fresh on every call, once or twice per sub-basin (`calculateRealBasin`/
 * `applySnowModel`). Real gap found by deep-review (2026-08-29): under
 * calibration (`runCalibration.ts`, ~300 evaluations per run) at Duero scale
 * (1728 sub-basins), that's ~200M redundant comparisons on the main thread.
 *
 * Keyed by the definitions array's own identity rather than threaded through
 * every function signature down to `valueByZone` — `project.parameterDefinitions`/
 * `stateVariableDefinitions` are replaced wholesale on any real edit (never
 * mutated in place, same as every other array on `EvalHidProject`), so a
 * `WeakMap` naturally invalidates itself: a new array after an edit gets a
 * fresh entry, and the old array's cached index becomes unreachable and
 * garbage-collected on its own, with no manual cache-clearing anywhere.
 */
const definitionIndexCache = new WeakMap<object[], Map<string, unknown[]>>();

function definitionsByModelKind<TDef extends { modelTypeCode: number; kind: number }>(
  definitions: TDef[]
): Map<string, TDef[]> {
  const cached = definitionIndexCache.get(definitions);
  if (cached) return cached as Map<string, TDef[]>;
  const index = new Map<string, TDef[]>();
  for (const d of definitions) {
    const key = `${d.modelTypeCode}:${d.kind}`;
    const bucket = index.get(key);
    if (bucket) bucket.push(d);
    else index.set(key, [d]);
  }
  definitionIndexCache.set(definitions, index as Map<string, unknown[]>);
  return index;
}

interface ZoneValueAccessors<TDef, TVal> {
  nameOf: (def: TDef) => string;
  codeOf: (def: TDef) => number;
  valueCodeOf: (val: TVal) => number;
  valueOf: (val: TVal) => number | null;
}

/** Value of a parameter/state variable for a zone: the one from the value table if it exists, otherwise its definition's default value. */
function valueByZone<
  TDef extends { modelTypeCode: number; kind: number; defaultValue: number | null },
  TVal extends { zoneCode: number | null; kind: number },
>(
  definitions: TDef[],
  valueIndex: ZoneValueIndex<TVal>,
  modelTypeCode: number,
  kind: number,
  zoneCode: number | null,
  { nameOf, codeOf, valueCodeOf, valueOf }: ZoneValueAccessors<TDef, TVal>
): Map<string, number> {
  const defs = definitionsByModelKind(definitions).get(`${modelTypeCode}:${kind}`) ?? [];
  const byCode = new Map(
    (valueIndex.get(`${kind}:${zoneCode}`) ?? []).map((v) => [valueCodeOf(v), valueOf(v)])
  );

  const result = new Map<string, number>();
  for (const def of defs) {
    const value = byCode.get(codeOf(def)) ?? def.defaultValue;
    if (value != null) result.set(nameOf(def), value);
  }
  return result;
}

function zoneParameters(
  project: EvalHidProject,
  parameterValueIndex: ZoneValueIndex<ParameterValue>,
  zoneCode: number | null,
  modelTypeCode: number,
  kind: number
): Map<string, number> {
  return valueByZone(
    project.parameterDefinitions,
    parameterValueIndex,
    modelTypeCode,
    kind,
    zoneCode,
    {
      nameOf: (d) => d.name,
      codeOf: (d) => d.parameterCode,
      valueCodeOf: (v) => v.parameterCode,
      valueOf: (v) => v.value,
    }
  );
}

function zoneStateVariables(
  project: EvalHidProject,
  stateVariableValueIndex: ZoneValueIndex<StateVariableValue>,
  zoneCode: number | null,
  modelTypeCode: number,
  kind: number
): Map<string, number> {
  return valueByZone(
    project.stateVariableDefinitions,
    stateVariableValueIndex,
    modelTypeCode,
    kind,
    zoneCode,
    {
      nameOf: (d) => d.name,
      codeOf: (d) => d.stateVariableCode,
      valueCodeOf: (v) => v.stateVariableCode,
      valueOf: (v) => v.value,
    }
  );
}

/**
 * Approximates "storage capacity" and "recession coefficient" from the
 * model's real parameters, without knowing which model they belong to: the
 * capacity is the largest parameter in mm (usually the main storage one in
 * EvalHid's models), the recession the smallest "1/time" parameter (there's
 * always one: Alfa, K0-K2, Kb, Kperc...). Only used as a generic
 * approximation for models that don't have a real engine yet.
 */
function capacityAndRecession(
  params: Map<string, number>,
  definitions: { name: string; units: string | null }[]
): { capacity: number; recession: number } {
  const unitByName = new Map(definitions.map((d) => [d.name, d.units]));

  let capacity = -Infinity;
  let recession = Infinity;
  for (const [name, value] of params) {
    const unit = unitByName.get(name);
    if (unit === "mm" && value > capacity) capacity = value;
    if (unit === "1/tiempo" && value > 0 && value < recession) recession = value;
  }
  return {
    capacity: Number.isFinite(capacity) && capacity > 0 ? capacity : DEFAULT_CAPACITY_MM,
    recession: Number.isFinite(recession) ? recession : DEFAULT_RECESSION,
  };
}

/** Daily contribution series (mm/day) of a sub-basin, with the generic reservoir setup (models not implemented for real yet). */
function simulateGenericBasin(
  precip: Float64Array,
  etp: Float64Array,
  capacity: number,
  recession: number
): Float64Array {
  const n = precip.length;
  const contribution = new Float64Array(n);
  let moisture = capacity * 0.3;
  let reservoir = 0;

  for (let t = 0; t < n; t++) {
    const p = Number.isFinite(precip[t]) ? precip[t] : 0;
    const e = Number.isFinite(etp[t]) ? etp[t] : 0;

    moisture += p;
    const realEtp = e * Math.min(1, moisture / capacity);
    moisture = Math.max(0, moisture - realEtp);

    const surplus = Math.max(0, moisture - capacity);
    moisture = Math.min(moisture, capacity);

    const fast = surplus * FAST_RUNOFF_FRACTION;
    reservoir += surplus * (1 - FAST_RUNOFF_FRACTION);
    const reservoirOutflow = reservoir * recession;
    reservoir -= reservoirOutflow;

    contribution[t] = fast + reservoirOutflow;
  }

  return contribution;
}

function adjust(series: Float64Array, factor: number): Float64Array {
  if (factor === 1) return series;
  return series.map((v) => v * factor);
}

interface RealSubBasinResult {
  contribution: Float64Array;
  detail: Record<string, Float64Array>;
  model: RealModelName;
}

interface SnowStepResult {
  /** What the rainfall-runoff model actually receives — `snowResult.liquidPrecipitation` if a snow model ran, the raw adjusted precipitation otherwise. */
  precipForModel: Float64Array;
  /** Snow model's own step detail, prefixed `snow_` — empty when no snow model ran. */
  snowDetail: Record<string, Float64Array>;
}

/**
 * Runs the sub-basin's snow model (if assigned and usable) over the
 * adjusted precipitation/temperature. Falls through to the raw adjusted
 * precipitation with no snow detail when there's no snow model, or a
 * temperature series is missing — same as `calculateRealBasin`'s own
 * fallback: the rainfall-runoff model still runs on raw precipitation
 * rather than throwing away a real result over the missing snow step.
 */
function applySnowModel(
  project: EvalHidProject,
  parameterValueIndex: ZoneValueIndex<ParameterValue>,
  stateVariableValueIndex: ZoneValueIndex<StateVariableValue>,
  subBasin: SubBasin,
  snowModel: RealSnowModel | null,
  adjustedPrecip: Float64Array,
  temp: Float64Array | null
): SnowStepResult {
  if (!snowModel || !temp) return { precipForModel: adjustedPrecip, snowDetail: {} };
  const adjustedTemp = adjust(temp, subBasin.tempAdjustment);
  const snowParams = zoneParameters(
    project,
    parameterValueIndex,
    subBasin.snowZoneCode,
    subBasin.snowModelTypeCode,
    1
  );
  const snowState = zoneStateVariables(
    project,
    stateVariableValueIndex,
    subBasin.snowZoneCode,
    subBasin.snowModelTypeCode,
    1
  );
  const snowResult = snowModel.calculate(adjustedPrecip, adjustedTemp, snowParams, snowState);
  return {
    precipForModel: snowResult.liquidPrecipitation,
    snowDetail: Object.fromEntries(
      Object.entries(snowResult.detail).map(([name, series]) => [`snow_${name}`, series])
    ),
  };
}

/** Real contribution (Témez, SAC, HBV, GR2M, GR4J or AWBM, with optional Nieve1/Nieve2) if the sub-basin uses already-implemented models; null otherwise. */
function calculateRealBasin(
  project: EvalHidProject,
  parameterValueIndex: ZoneValueIndex<ParameterValue>,
  stateVariableValueIndex: ZoneValueIndex<StateVariableValue>,
  subBasin: SubBasin,
  precip: Float64Array,
  etp: Float64Array,
  temp: Float64Array | null,
  pumping: Float64Array | null,
  realCodes: Map<string, number>,
  realSnowCodes: Map<string, number>
): RealSubBasinResult | null {
  const model = REAL_MODELS.find((m) => realCodes.get(m.modelTypeName) === subBasin.modelTypeCode);
  if (!model) return null;

  const usesSnow = subBasin.snowModelTypeCode !== NO_MODEL;
  const snowModel = usesSnow
    ? REAL_SNOW_MODELS.find(
        (m) => realSnowCodes.get(m.modelTypeName) === subBasin.snowModelTypeCode
      )
    : null;
  // If a snow model is assigned but can't actually run (not implemented, or
  // the temperature series is missing — flagged separately, see
  // validations.ts' sinTempAviso/subBasinState.ts' sinColumnaTemp), only the
  // snow step is skipped: the sub-basin's own rainfall-runoff model still
  // runs on the raw precipitation below, same as a sub-basin with no snow
  // zone at all. Falling back to the fully generic reservoir approximation
  // here would throw away a real result that precipitation/ETP alone are
  // enough to compute.

  const params = zoneParameters(
    project,
    parameterValueIndex,
    subBasin.zoneCode,
    subBasin.modelTypeCode,
    0
  );
  const state = zoneStateVariables(
    project,
    stateVariableValueIndex,
    subBasin.zoneCode,
    subBasin.modelTypeCode,
    0
  );

  const adjustedPrecip = adjust(precip, subBasin.precipAdjustment);
  const adjustedEtp = adjust(etp, subBasin.etpAdjustment);

  const { precipForModel, snowDetail } = applySnowModel(
    project,
    parameterValueIndex,
    stateVariableValueIndex,
    subBasin,
    snowModel ?? null,
    adjustedPrecip,
    temp
  );

  const hasAquifer = subBasin.aquiferCode !== 0;
  const modelResult = model.calculate(
    precipForModel,
    adjustedEtp,
    params,
    state,
    hasAquifer,
    pumping
  );
  return {
    contribution: modelResult.contribution,
    detail: { ...modelResult.detail, ...snowDetail },
    model: model.modelTypeName,
  };
}

// Columns are linked by the index already persisted on the sub-basin
// (SubBasin.precipCol/etpCol/tempCol, 1-based; 0 = unlinked) — resolved by
// name only once, when the project is opened or a series is
// uploaded/pasted (see resolveIndicesByName, dailySeries.ts), never here.
// Existence check goes through hasColumnAt (shared with
// validations.ts/subBasinState.ts) rather than its own `values.length`
// check, so there's only one definition of "does this column exist" to
// keep in sync with `columns`/`values` — see hasColumnAt's own comment.
export function basinColumn(colIndex: number, series: DailySeries): Float64Array | null {
  return hasColumnAt(series, colIndex) ? series.values[colIndex - 1] : null;
}

/**
 * Converts a sub-basin's raw pumping column (a volume, in the project's
 * active unit — e.g. Hm³, same as `Ent_Bomb.csv`'s real convention) into
 * mm/step, the same scale as precipitation/ETP that each model subtracts
 * it from — `SeriesTemporales.cs`'s `actualiza_bomb`: `C[i].bomb =
 * valor/superficie/G.unid`. Unlike a sub-basin, an AQUIFER's own pumping
 * (`Aquifer.pumpingCol`) is used raw, with no conversion — an aquifer has
 * no area of its own and already works in volume (see aquifer.ts's header,
 * and aggregation.ts's aggregateContributionsByAquifer, which reads
 * `a.pumpingCol` directly without calling this).
 */
function subBasinPumpingDepth(raw: Float64Array, area: number, coefficient: number): Float64Array {
  const divisor = area * coefficient;
  const depth = new Float64Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    depth[i] = raw[i] / divisor;
  }
  return depth;
}

/**
 * The first step where either column is invalid/empty (`NaN`,
 * `dailySeries.ts`'s parsing convention for both a blank cell and a
 * non-numeric one — see memory `project_evalhid_series_gaps`) —
 * `precip.length` if neither column ever is, meaning nothing to truncate.
 * A lumped model carries state from one step to the next (soil moisture,
 * reservoir levels, snow pack...), so a gap can't be skipped and resumed
 * from — the state at the resume point would be unknown. Truncating at the
 * FIRST invalid step (rather than only excluding that one bad step, or the
 * sub-basin entirely) is the only choice that's both physically sound and
 * still produces a real, usable partial result for whatever prefix IS
 * complete — see `calculateOneSubBasin` below.
 */
function firstInvalidIndex(precip: Float64Array, etp: Float64Array): number {
  const len = precip.length;
  for (let i = 0; i < len; i++) {
    if (!Number.isFinite(precip[i]) || !Number.isFinite(etp[i])) return i;
  }
  return len;
}

/** `short` (a sub-basin's result, truncated at its own `firstInvalidIndex`) padded back out to `fullLength` with NaN — so every consumer that assumes `result.contribution.length` is the SAME for every sub-basin (aggregation, the results table, CSV export, charts) keeps working unchanged. NaN (not 0) marks "not computed" — aggregateByKey (aggregation.ts) and groupByPeriod (aggregates.ts) already treat a NaN cell as "no contribution from this sub-basin at this step" rather than a real zero, the same convention a raw invalid input cell already uses. */
function padToFullLength(short: Float64Array, fullLength: number): Float64Array {
  if (short.length === fullLength) return short;
  const padded = new Float64Array(fullLength).fill(NaN);
  padded.set(short);
  return padded;
}

function padDetail(
  detail: Record<string, Float64Array>,
  fullLength: number
): Record<string, Float64Array> {
  const padded: Record<string, Float64Array> = {};
  for (const [key, series] of Object.entries(detail))
    padded[key] = padToFullLength(series, fullLength);
  return padded;
}

export interface SubBasinTruncation {
  subBasin: SubBasin;
  /** ISO date of the first invalid/empty precipitation or ETP cell — everything from here on is NOT part of the result (see `padToFullLength`). */
  gapStartDate: string;
  /** How many of the calculated range's steps came before the gap — 0 means no valid data at all (the sub-basin has no result whatsoever, see `calculateOneSubBasin`'s own early return). */
  validSteps: number;
}

/**
 * Sub-basins truncated (or, if `validSteps` is 0, excluded entirely) because
 * their own precipitation/ETP column has an invalid/empty cell somewhere
 * within the range being calculated — distinct from `validations.ts`'s "no
 * columns" notice (a column that doesn't exist at all, which this
 * deliberately skips: already covered there). Mirrors exactly the
 * truncation `calculateOneSubBasin` itself applies below — kept separate
 * (not returned alongside `SubBasinResult[]`) so
 * `calculateProject`/`calculateProjectAsync`'s return type stays unchanged
 * for their many existing callers (calibration, tests). Meant to be called
 * by `useCalculation.ts`'s `runCalculation` on the SAME already-date-trimmed
 * `precipitation`/`etp` about to be handed to the engine, so this can
 * never disagree with what actually got truncated — a sub-basin whose bad
 * data falls outside the confirmed calculation range isn't flagged.
 */
export function findTruncatedSubBasins(
  subBasins: SubBasin[],
  precipitation: DailySeries,
  etp: DailySeries
): SubBasinTruncation[] {
  const result: SubBasinTruncation[] = [];
  for (const c of subBasins) {
    if (c.modelTypeCode === NO_MODEL) continue;
    const precipSeries = basinColumn(c.precipCol, precipitation);
    const etpSeries = basinColumn(c.etpCol, etp);
    if (!precipSeries || !etpSeries) continue;
    const cutoff = firstInvalidIndex(precipSeries, etpSeries);
    if (cutoff < precipitation.dates.length) {
      result.push({ subBasin: c, gapStartDate: precipitation.dates[cutoff], validSteps: cutoff });
    }
  }
  return result;
}

/**
 * One sub-basin's worth of `calculateProject`'s loop body — pulled out so
 * both the sync version (below, used as-is hundreds of times per
 * calibration run, see runCalibration.ts) and the async/progress version
 * (`calculateProjectAsync`) share the exact same per-sub-basin logic instead
 * of two copies drifting apart. `null` when the sub-basin has no linked
 * precipitation/ETP column (skipped, not an error — same as the old
 * `continue`), or when that column exists but has NO valid data at all
 * within the calculated range (`firstInvalidIndex` is 0). Otherwise, the
 * model only runs for the valid PREFIX (`[0, firstInvalidIndex)` — see that
 * function's own header for why a gap can't just be skipped over), and the
 * result is padded back out to the full range with NaN
 * (`padToFullLength`) — `findTruncatedSubBasins` above is how the caller
 * finds out which sub-basins got truncated (or excluded) and warns, since
 * this function alone doesn't say why.
 */
function calculateOneSubBasin(
  project: EvalHidProject,
  parameterValueIndex: ZoneValueIndex<ParameterValue>,
  stateVariableValueIndex: ZoneValueIndex<StateVariableValue>,
  subBasin: SubBasin,
  precipitation: DailySeries,
  etp: DailySeries,
  temperature: DailySeries | null,
  pumping: DailySeries | null,
  realCodes: Map<string, number>,
  realSnowCodes: Map<string, number>,
  // Reports the SAME truncation `findTruncatedSubBasins` would, from the
  // exact same `firstInvalidIndex` call this function already makes for its
  // own purposes — real gap found by deep-review (2026-08-29):
  // `useCalculation.ts`'s `runCalculation` used to run `findTruncatedSubBasins`
  // as a full separate pre-scan (same O(sub-basins × timesteps) cost) just
  // to get this same information a split second before `calculateProjectAsync`
  // recomputed it anyway, per sub-basin, right here. Optional so every other
  // caller (`calculateProject`, `calculateOneSubBasinDetail`, calibration)
  // stays unaffected.
  onTruncated?: (info: SubBasinTruncation) => void
): SubBasinResult | null {
  const precipSeries = basinColumn(subBasin.precipCol, precipitation);
  const etpSeries = basinColumn(subBasin.etpCol, etp);
  if (!precipSeries || !etpSeries) return null;

  const cutoff = firstInvalidIndex(precipSeries, etpSeries);
  if (cutoff < precipitation.dates.length) {
    onTruncated?.({ subBasin, gapStartDate: precipitation.dates[cutoff], validSteps: cutoff });
  }
  if (cutoff === 0) return null; // no valid data at all in the calculated range
  const precipForCalc =
    cutoff < precipSeries.length ? precipSeries.subarray(0, cutoff) : precipSeries;
  const etpForCalc = cutoff < etpSeries.length ? etpSeries.subarray(0, cutoff) : etpSeries;

  const tempSeries = temperature ? basinColumn(subBasin.tempCol, temperature) : null;
  const rawPumpingSeries = pumping ? basinColumn(subBasin.pumpingCol, pumping) : null;
  const pumpingSeries = rawPumpingSeries
    ? subBasinPumpingDepth(rawPumpingSeries, subBasin.area, activeUnitsCoefficient(project))
    : null;

  const realResult = calculateRealBasin(
    project,
    parameterValueIndex,
    stateVariableValueIndex,
    subBasin,
    precipForCalc,
    etpForCalc,
    tempSeries,
    pumpingSeries,
    realCodes,
    realSnowCodes
  );

  let contribution: Float64Array;
  let detail: Record<string, Float64Array> | undefined;
  let model: RealModelName | undefined;
  if (realResult) {
    contribution = realResult.contribution;
    detail = realResult.detail;
    model = realResult.model;
  } else {
    const definitions =
      definitionsByModelKind(project.parameterDefinitions).get(`${subBasin.modelTypeCode}:0`) ?? [];
    const params = zoneParameters(
      project,
      parameterValueIndex,
      subBasin.zoneCode,
      subBasin.modelTypeCode,
      0
    );
    const { capacity, recession } = capacityAndRecession(params, definitions);
    contribution = simulateGenericBasin(precipForCalc, etpForCalc, capacity, recession);
  }

  const fullLength = precipitation.dates.length;
  const paddedDetail = detail ? padDetail(detail, fullLength) : undefined;
  return {
    subBasinCode: subBasin.subBasinCode,
    description: subBasin.description,
    contribution: padToFullLength(contribution, fullLength),
    detail: paddedDetail,
    model,
    aggregationDetail: computeAggregationDetail(model, paddedDetail, fullLength),
  };
}

/**
 * Recomputes ONE sub-basin's full result — WITH its complete step-by-step
 * `detail`, unlike `calculateProjectAsync`'s own bulk path (see
 * `SubBasinResult.detail`'s own comment) — for `useSubBasinDetail.ts`'s
 * on-demand "Ver detalle" view: a huge project's bulk calculation never has
 * to retain every sub-basin's full detail just so one of them can be
 * inspected afterward. Rebuilds the same per-project indices
 * `calculateProject(Async)` already rebuild from scratch on every call
 * (cheap, nothing here is cached across calls today either) rather than
 * exposing `calculateOneSubBasin`'s own lower-level plumbing
 * (`ZoneValueIndex`, `indexByZone`...) outside this module. Returns `null`
 * for an unknown `subBasinCode`, or the same cases `calculateOneSubBasin`
 * itself does (no linked precipitation/ETP column, or no valid data at all
 * in range).
 */
export function calculateOneSubBasinDetail(
  project: EvalHidProject,
  subBasinCode: number,
  precipitation: DailySeries,
  etp: DailySeries,
  temperature: DailySeries | null,
  pumping?: DailySeries | null
): SubBasinResult | null {
  const subBasin = project.subBasins.find((s) => s.subBasinCode === subBasinCode);
  if (!subBasin) return null;
  const realCodes = realModelCodes(project);
  const realSnowCodes = realSnowModelCodes(project);
  const parameterValueIndex = indexByZone(project.parameterValues);
  const stateVariableValueIndex = indexByZone(project.stateVariableValues);
  return calculateOneSubBasin(
    project,
    parameterValueIndex,
    stateVariableValueIndex,
    subBasin,
    precipitation,
    etp,
    temperature,
    pumping ?? null,
    realCodes,
    realSnowCodes
  );
}

/** Calculates the daily contribution of every sub-basin in the project (see file header: real/generic hybrid). Synchronous — used as-is by test/engine.test.ts and by the calibration search (runCalibration.ts's `evaluate`, called up to hundreds of times per run, itself already wrapped in sceua.ts's own periodic yielding — see that file's header for why THIS function staying synchronous is the deliberate choice there). For the main "Calculate" button, which runs this ONCE per click and benefits from a responsive UI/progress on a large project instead, see `calculateProjectAsync` below. */
export function calculateProject(
  project: EvalHidProject,
  precipitation: DailySeries,
  etp: DailySeries,
  temperature: DailySeries | null,
  // Optional (defaults to `null`, i.e. no pumping applied) so every existing
  // caller that doesn't care about pumping (the calibration search,
  // most of engine.test.ts) doesn't need updating for this parameter.
  pumping?: DailySeries | null
): SubBasinResult[] {
  const realCodes = realModelCodes(project);
  const realSnowCodes = realSnowModelCodes(project);
  const parameterValueIndex = indexByZone(project.parameterValues);
  const stateVariableValueIndex = indexByZone(project.stateVariableValues);
  const results: SubBasinResult[] = [];
  for (const subBasin of project.subBasins) {
    const result = calculateOneSubBasin(
      project,
      parameterValueIndex,
      stateVariableValueIndex,
      subBasin,
      precipitation,
      etp,
      temperature,
      pumping ?? null,
      realCodes,
      realSnowCodes
    );
    if (result) results.push(result);
  }
  return results;
}

// Same idiom as sceua.ts's own `yieldToUI` (see its header comment for why):
// a plain macrotask, not needed by the calculation itself (each sub-basin's
// own work is synchronous), only so the browser gets a chance to repaint/
// respond to input between sub-basins instead of freezing the tab for the
// whole run.
const YIELD_EVERY_SUB_BASINS = 10;

function yieldToUI(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

/**
 * `calculateProject`, except async and yielding periodically (every
 * `YIELD_EVERY_SUB_BASINS` sub-basins) with progress reported via
 * `onProgress(done, total)` — used by the main "Calculate"/"Recalculate"
 * button (useCalculation.ts) instead of the sync version, which used to
 * freeze the tab for the whole run on a large project with no feedback.
 * `total` is `project.subBasins.length` (every sub-basin counts toward
 * progress, including ones later skipped for missing series — simplest
 * denominator, and `done` always reaches it).
 */
export async function calculateProjectAsync(
  project: EvalHidProject,
  precipitation: DailySeries,
  etp: DailySeries,
  temperature: DailySeries | null,
  // Same optional default as calculateProject's own `pumping` — see there.
  pumping?: DailySeries | null,
  opts?: {
    onProgress?: (done: number, total: number) => void;
    /** See calculateOneSubBasin's own `onTruncated` param — lets the caller (useCalculation.ts) collect the SAME truncation warning findTruncatedSubBasins would, without a separate full pre-scan. */
    onTruncated?: (info: SubBasinTruncation) => void;
  }
): Promise<SubBasinResult[]> {
  const realCodes = realModelCodes(project);
  const realSnowCodes = realSnowModelCodes(project);
  const parameterValueIndex = indexByZone(project.parameterValues);
  const stateVariableValueIndex = indexByZone(project.stateVariableValues);
  const total = project.subBasins.length;
  const results: SubBasinResult[] = [];
  let done = 0;
  for (const subBasin of project.subBasins) {
    const result = calculateOneSubBasin(
      project,
      parameterValueIndex,
      stateVariableValueIndex,
      subBasin,
      precipitation,
      etp,
      temperature,
      pumping ?? null,
      realCodes,
      realSnowCodes,
      opts?.onTruncated
    );
    // Drops the full step-by-step `detail` before retaining this result —
    // `aggregationDetail` (computed just above, inside calculateOneSubBasin)
    // already carries everything aggregation.ts needs from it. See
    // SubBasinResult.detail's own comment for the real memory incident this
    // avoids. `useSubBasinDetail.ts` recomputes one sub-basin's full detail
    // on demand instead, whenever the UI actually needs to show it.
    if (result) results.push({ ...result, detail: undefined });
    done++;
    opts?.onProgress?.(done, total);
    if (done % YIELD_EVERY_SUB_BASINS === 0) await yieldToUI();
  }
  return results;
}
